import axios from 'axios';
import { extract_items, parse_response_data } from '@/utils/api_response_helpers';
import { fetch_entity_type_workflows, fetch_sla_assigned_workflows } from '@/services/workflow_service';
import { LIST_SLA_CONFIGS_URL, get_request_config } from '@/utils/system_constants/constant.js';
import {
  build_sla_workflow_fields,
  get_first_workflow_assignment,
  normalize_workflow_option_value,
} from './service_layer_agreement.helpers';

const SORT_LABELS = {
  updated_desc: 'Newest Updated',
  updated_asc: 'Oldest Updated',
  name_asc: 'Name A-Z',
  name_desc: 'Name Z-A',
  created_asc: 'Oldest Created',
};

function build_search_text(sla = {}) {
  const search_text = [
    sla.name,
    sla.description,
    sla.target_duration,
    sla.warning_threshold,
    sla.escalation_duration,
  ].filter(Boolean).join(' ').toLowerCase();
  return search_text;
}

function matches_filter_mode(sla = {}, filter_mode = 'all') {
  const mode_rules = {
    all: true,
    active: sla.is_active !== false,
    inactive: sla.is_active === false,
  };
  const does_match = Reflect.get(mode_rules, filter_mode) ?? true;
  return does_match;
}

function get_updated_timestamp(item = {}) {
  const timestamp = new Date(item.updated_at || item.date_created || 0).getTime();
  return timestamp;
}

function get_name_value(item = {}) {
  const name_value = String(item.name || '').toLowerCase();
  return name_value;
}

function compare_by_updated(first, second, direction = 'desc') {
  const first_updated = get_updated_timestamp(first);
  const second_updated = get_updated_timestamp(second);
  const multiplier = direction === 'asc' ? 1 : -1;
  const comparison = (first_updated - second_updated) * multiplier;
  return comparison;
}

function compare_by_name(first, second, direction = 'asc') {
  const first_name = get_name_value(first);
  const second_name = get_name_value(second);
  const comparison = direction === 'asc'
    ? first_name.localeCompare(second_name)
    : second_name.localeCompare(first_name);
  return comparison;
}

function get_sort_comparison(first, second, sort_mode = 'updated_desc') {
  const comparators = {
    name_asc: () => compare_by_name(first, second, 'asc'),
    name_desc: () => compare_by_name(first, second, 'desc'),
    created_asc: () => compare_by_updated(first, second, 'asc'),
    updated_asc: () => compare_by_updated(first, second, 'asc'),
    updated_desc: () => compare_by_updated(first, second, 'desc'),
  };
  const comparator = Reflect.get(comparators, sort_mode) || comparators.updated_desc;
  const comparison = comparator();
  return comparison;
}

async function request_sla_config_items() {
  const response = await axios.get(LIST_SLA_CONFIGS_URL, get_request_config());
  const response_data = parse_response_data(response);
  const response_items = extract_items(response);
  const sla_configs = Array.isArray(response_data?.sla_configs) ? response_data.sla_configs : [];
  const extra_items = Array.isArray(response_data?.items) ? response_data.items : [];
  const items = response_items.concat(sla_configs, extra_items);
  return items;
}

function get_sla_config_error_message(error) {
  const error_message = error?.response?.data?.message || error?.message || 'Failed to load SLA configs.';
  return error_message;
}

function normalize_workflow_options(workflows = []) {
  const normalized_workflows = (Array.isArray(workflows) ? workflows : [])
    .map((workflow, index) => normalize_workflow_option_value(workflow, index))
    .filter((workflow) => workflow.id);
  return normalized_workflows;
}

function get_workflow_options_error_message(error) {
  const error_message = error?.response?.data?.message || error?.message || 'Failed to load workflow options.';
  return error_message;
}

export function filter_slas(sla_configs = [], search_query = '', filter_mode = 'all') {
  const query = String(search_query || '').trim().toLowerCase();
  const filtered_items = sla_configs.filter((sla) => {
    const mode_match = matches_filter_mode(sla, filter_mode);
    if (!mode_match) return false;
    if (!query) return true;
    return build_search_text(sla).includes(query);
  });
  return filtered_items;
}

export function sort_slas(sla_configs = [], sort_mode = 'updated_desc') {
  const sorted_items = sla_configs.slice().sort((first, second) => get_sort_comparison(first, second, sort_mode));
  return sorted_items;
}

export function get_sort_label(sort_mode = 'updated_desc') {
  const sort_label = Reflect.get(SORT_LABELS, sort_mode) || SORT_LABELS.updated_desc;
  return sort_label;
}

export async function fetch_sla_configs_for_vm(vm) {
  vm.loading = true;
  try {
    const items = await request_sla_config_items();
    const enriched_items = await Promise.all(items.map((item) => vm.enrich_sla_config_item(item)));
    vm.sla_configs = enriched_items;
  } catch (error) {
    vm.sla_configs = [];
    vm.show_error(get_sla_config_error_message(error));
  } finally {
    vm.loading = false;
  }
}

export async function fetch_workflow_options_for_vm(vm) {
  try {
    const workflows = await fetch_entity_type_workflows();
    const normalized_workflows = normalize_workflow_options(workflows);
    vm.workflow_options = normalized_workflows;
  } catch (error) {
    vm.workflow_options = [];
    vm.show_error(get_workflow_options_error_message(error));
  }
}

export async function enrich_sla_config_item_for_vm(item = {}, vm) {
  const assigned_workflows = await fetch_sla_assigned_workflows(item?.id).catch(() => []);
  const assigned_workflow = get_first_workflow_assignment(assigned_workflows);
  const enriched_item = vm.normalize_sla_item({
    ...item,
    ...build_sla_workflow_fields(item, assigned_workflow),
  });
  return enriched_item;
}
